import { Box, Button, Container, Grid, Stack, Typography } from '@mui/material'
import { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { TypeAnimation } from 'react-type-animation'
import ProjectCard from '../components/ProjectCard'
import { divingDadsMeta } from '../content/divingDads'
import {
  projectCardDescription,
  useWoodworkProjects,
  woodworkProjectPath
} from '../api-hooks/useWoodworkProjects'
import { formatProjectDateRange } from '../content/formatProjectDate'
import { bio, labProjects } from '../content/site'
import WoodworkLogo from '../Work/WoodworkLogo'
import '../Work/WoodworkLogo.css'
import './Home.css'

const Home = () => {
  const { projects: woodworkProjects, statusLabel } = useWoodworkProjects()
  const [taglineVisible, setTaglineVisible] = useState(false)

  return (
    <>
      <section className='hero'>
        <Box className='hero__inner'>
          <p className='hero__eyebrow'>{bio.tagline}</p>
          <TypeAnimation
            sequence={[
              bio.name,
              400,
              () => {
                setTaglineVisible(true)
              }
            ]}
            wrapper='h1'
            speed={40}
            className='hero__title'
            cursor={false}
          />
          <Box className='hero__tagline' aria-live='polite'>
            {taglineVisible && (
              <TypeAnimation
                sequence={[
                  'Furniture & casework',
                  1400,
                  'Shop builds',
                  1400,
                  'Honest materials',
                  1400,
                  'Side projects in the Lab',
                  1400
                ]}
                wrapper='span'
                speed={40}
                deletionSpeed={55}
                repeat={Infinity}
                cursor={false}
              />
            )}
          </Box>
          <Typography className='hero__intro' component='p'>
            {bio.intro}
          </Typography>
          <Stack
            className='hero__actions'
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ opacity: taglineVisible ? 1 : 0, transition: 'opacity 0.6s ease 0.3s' }}
          >
            <Button
              component={RouterLink}
              to='/work'
              variant='contained'
              color='primary'
              size='large'
              sx={{ px: 4, py: 1.25 }}
            >
              View Work
            </Button>
            <Button
              component={RouterLink}
              to='/lab'
              variant='outlined'
              size='large'
              sx={{
                px: 4,
                py: 1.25,
                borderColor: 'rgba(255, 255, 255, 0.35)',
                color: 'text.primary',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'rgba(201, 162, 39, 0.08)'
                }
              }}
            >
              Explore Lab
            </Button>
            <Button
              component={RouterLink}
              to='/diving-dads'
              variant='outlined'
              size='large'
              sx={{
                px: 4,
                py: 1.25,
                borderColor: 'rgba(62, 184, 196, 0.45)',
                color: '#b8e8ee',
                '&:hover': {
                  borderColor: '#3eb8c4',
                  backgroundColor: 'rgba(62, 184, 196, 0.1)'
                }
              }}
            >
              Diving Dads
            </Button>
          </Stack>
        </Box>
      </section>

      <Container maxWidth='lg' sx={{ px: { xs: 2, sm: 3 }, pb: 6 }}>
        <Box component='section' id='about' className='home-section'>
          <p className='section-eyebrow'>About</p>
          <h2 className='section-heading'>At the bench</h2>
          {bio.about.map((paragraph) => (
            <Typography
              key={paragraph.slice(0, 32)}
              variant='body1'
              color='text.secondary'
              paragraph
              sx={{ maxWidth: '65ch', lineHeight: 1.8 }}
            >
              {paragraph}
            </Typography>
          ))}
        </Box>

        <Box component='section' id='work-preview' className='home-section'>
          <p className='section-eyebrow work-brand-lockup'>
            <WoodworkLogo variant='leaf' size='accent' aria-hidden />
            Work
          </p>
          <h2 className='section-heading'>Woodworking</h2>
          <p className='section-lead'>
            Builds from the shop — documented as photos become available.
          </p>
          <Grid container spacing={3}>
            {woodworkProjects.slice(0, 2).map((project) => (
              <Grid item xs={12} sm={6} key={project.id}>
                <ProjectCard
                  title={project.title}
                  description={projectCardDescription(project)}
                  materials={project.materials}
                  status={statusLabel[project.status]}
                  date={formatProjectDateRange(project.startDate, project.endDate)}
                  imageSrc={project.imageSrc}
                  imageAlt={project.imageAlt}
                  to={project.hasGallery ? woodworkProjectPath(project.id) : undefined}
                />
              </Grid>
            ))}
          </Grid>
          <Button
            component={RouterLink}
            to='/work'
            sx={{ mt: 3, color: 'primary.main' }}
          >
            See all work →
          </Button>
        </Box>

        <Box component='section' id='lab-preview' className='home-section'>
          <p className='section-eyebrow'>Lab</p>
          <h2 className='section-heading'>Side experiments</h2>
          <p className='section-lead'>
            Small tools and utilities — useful, odd, or both.
          </p>
          <Grid container spacing={3}>
            {labProjects.map((project) => (
              <Grid item xs={12} sm={6} md={4} key={project.id}>
                <ProjectCard
                  title={project.title}
                  description={project.description}
                  tag={project.tag}
                  to={project.path}
                  placeholderLabel='Open project'
                />
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box component='section' id='diving-dads-preview' className='home-section'>
          <p className='section-eyebrow' style={{ color: '#3eb8c4' }}>
            Pool deck
          </p>
          <h2 className='section-heading'>{divingDadsMeta.title}</h2>
          <p className='section-lead'>{divingDadsMeta.intro}</p>
          <Button
            component={RouterLink}
            to='/diving-dads'
            sx={{ color: '#3eb8c4', '&:hover': { backgroundColor: 'rgba(62, 184, 196, 0.1)' } }}
          >
            Meet the Diving Dads →
          </Button>
        </Box>
      </Container>
    </>
  )
}

export default Home
